import React, { useState, useEffect } from "react";
import { Moon, Sun, Menu, X, Users, Shield } from "lucide-react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
import { initGA, logPageView } from "./services/analyticsService";
import DevTools from "./components/DevTools";
import AgileTools from "./components/AgileTools";
import IceBreakers from "./components/IceBreakers";
import Roulette from "./components/Roulette";
import Sidebar from "./components/Sidebar";
import XPPractices from "./components/XPPractices";
import TeamNameGenerator from "./components/TeamNameGenerator";
import CookieBanner from "./components/CookieBanner";
import DonationPopup from "./components/DonationPopup";
import AdSenseAd from "./components/AdSenseAd";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Guides from "./pages/Guides";
import { getTeamMembers, removeTeamMember } from "./services/teamService";

// Componente para mostrar los miembros del equipo compartidos
const TeamMembersList: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<string[]>(getTeamMembers());
  const [teamName, setTeamName] = useState<string>("");
  const [confirmedTeamName, setConfirmedTeamName] = useState<string>("");

  useEffect(() => {
    const handleTeamMembersUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.members) {
        setTeamMembers(customEvent.detail.members);
      }
    };

    const handleTeamNameSelected = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.teamName) {
        setConfirmedTeamName(customEvent.detail.teamName);
      }
    };

    window.addEventListener("team-members-updated", handleTeamMembersUpdate);
    window.addEventListener("team-name-selected", handleTeamNameSelected);

    return () => {
      window.removeEventListener(
        "team-members-updated",
        handleTeamMembersUpdate,
      );
      window.removeEventListener("team-name-selected", handleTeamNameSelected);
    };
  }, []);

  const handleRemoveMember = (member: string) => {
    const updatedMembers = removeTeamMember(member);
    setTeamMembers(updatedMembers);
  };

  const handleTeamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTeamName(e.target.value);
  };

  const confirmTeamName = () => {
    if (teamName.trim()) {
      setConfirmedTeamName(teamName.trim());
      setTeamName("");
    }
  };

  // Si no hay miembros, no mostrar nada
  if (teamMembers.length === 0) return null;

  return (
    <div className="bg-blue-50 dark:bg-blue-900/30 px-4 py-2">
      <div className="container mx-auto">
        <div className="flex items-center mb-3">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-blue-600 dark:text-blue-400" />
            <span className="font-semibold text-blue-600 dark:text-blue-400">
              Team Members ({teamMembers.length})
            </span>

            {confirmedTeamName ? (
              <div className="ml-2 relative overflow-hidden rounded-lg shadow-md transform transition-transform hover:scale-105 hover:shadow-lg">
                {/* Animated gradient background */}
                <div
                  className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-500 to-blue-600 
                              dark:from-blue-500 dark:via-purple-400 dark:to-blue-500
                              animate-shimmer bg-size-200"
                ></div>

                {/* Text content */}
                <div className="relative px-4 py-1.5 text-white font-medium">
                  {confirmedTeamName}
                </div>
              </div>
            ) : (
              <div className="ml-2 flex">
                <input
                  type="text"
                  value={teamName}
                  onChange={handleTeamNameChange}
                  placeholder="Set team name"
                  className="w-40 px-2 py-1 text-sm border border-blue-200 dark:border-blue-700 rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
                <button
                  onClick={confirmTeamName}
                  className="px-2 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-r-md border border-blue-500"
                >
                  Set
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {teamMembers.map((member) => (
            <div
              key={member}
              className="bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full text-sm shadow-sm flex items-center"
            >
              <span className="text-gray-800 dark:text-white">{member}</span>
              <button
                onClick={() => handleRemoveMember(member)}
                className="ml-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                title="Remove member"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : true;
  });
  const location = useLocation();

  useEffect(() => {
    // Inicializar Google Analytics
    initGA("G-XXXXXXXXXX"); // Reemplaza con tu ID de Google Analytics
    logPageView(); // Log de la página inicial
  }, []);

  useEffect(() => {
    // Log de cambios de página
    logPageView();
  }, [location]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("dev");

  useEffect(() => {
    // Actualizar activeSection basado en la ruta actual
    const path = location.pathname.slice(1) || "dev";
    setActiveSection(path);
  }, [location]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    localStorage.setItem("darkMode", JSON.stringify(!isDarkMode));
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    navigate(`/${section}`);
    setIsMenuOpen(false);
  };

  return (
    <div
      className={`min-h-screen ${isDarkMode ? "dark bg-gray-900" : "bg-gray-50"} flex justify-center items-start`}
    >
      <div className="w-full max-w-[1920px] mx-auto min-h-screen max-h-[2000px] flex flex-col relative shadow-xl">
        {/* Header */}
        <header className="sticky top-0 w-full bg-white dark:bg-gray-800 shadow-md z-50">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden text-gray-600 dark:text-gray-200"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div className="flex items-center">
                <Users size={24} className="text-blue-500 mr-2" />
                <h1 className="text-xl font-bold text-gray-800 dark:text-white">
                  devagile.tools
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to="/privacy-policy"
                className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Privacy Policy"
              >
                <Shield size={16} />
                <span className="hidden sm:inline">Privacy</span>
              </Link>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isDarkMode ? (
                  <Sun className="text-yellow-400" size={24} />
                ) : (
                  <Moon className="text-gray-600" size={24} />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Donation Popup */}
        <DonationPopup />

        {/* Team Members List */}
        <div className="w-full">
          <TeamMembersList />
        </div>

        {/* Main Content */}
        <div className="lg:flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <Sidebar
            isOpen={isMenuOpen}
            setIsOpen={setIsMenuOpen}
            activeSection={activeSection}
            setActiveSection={handleSectionChange}
          />

          {/* Main Content Area */}
          <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
            <Routes>
              <Route path="/" element={<DevTools />} />
              <Route path="/dev" element={<DevTools />} />
              <Route path="/agile" element={<AgileTools />} />
              <Route path="/xp" element={<XPPractices />} />
              <Route path="/teamname" element={<TeamNameGenerator />} />
              <Route path="/ice" element={<IceBreakers />} />
              <Route path="/roulette" element={<Roulette />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Routes>
          </main>

          {/* Support Sidebar */}
          <aside className="hidden lg:block w-80 p-4 bg-white dark:bg-gray-800">
            <div className="sticky top-20 space-y-6">
              {/* Ko-fi Support Widget */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 rounded-lg p-6 text-center shadow-lg">
                <h3 className="font-bold text-gray-800 dark:text-white mb-2">
                  Support DevAgile Tools
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Help us keep these tools free and continuously improve them
                </p>
                <div className="space-y-3">
                  <a
                    href="https://ko-fi.com/rubendepaz1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 px-4 bg-[#00b9fe] hover:bg-[#0099d4] text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    ☕ Support on Ko-fi
                  </a>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Your support helps us maintain and improve these developer
                    tools
                  </p>
                </div>
              </div>

              {/* Google AdSense Ad */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-2">
                  Advertisement
                </p>
                <AdSenseAd
                  adSlot="1234567890"
                  className="w-full"
                  style={{ display: "block", minHeight: "250px" }}
                />
              </div>
            </div>
          </aside>
        </div>

        {/* Cookie Banner */}
        <CookieBanner />
      </div>
    </div>
  );
}

export default App;
